import React, { useState, useEffect } from 'react';
import { TabsContent, Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardHeader, CardContent, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Download, Filter, X, FilterX, Calendar, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { TableContainer } from '../components/ui/table-container';
import { fetchActivityLogs, fetchSystemLogs, exportActivityLogsCSV, exportSystemLogsCSV, LogFilters, LogDateGroup, ActivityLog, SystemLog } from '../services/logService';
import { useTheme } from '../contexts/ThemeContext';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';

// Common type that works for both log types
type AnyLogDateGroup = LogDateGroup<ActivityLog> | LogDateGroup<SystemLog>;

// Predefined mock data - moved outside to ensure it's always available
const MOCK_USERS = [
  { id: 1, username: 'admin' },
  { id: 2, username: 'debater1' },
  { id: 3, username: 'adjudicator1' },
];

const MOCK_ROUNDS = [
  { id: 1, round_code: 'R1001', motion: { id: 1, text: 'This house believes...' } },
  { id: 2, round_code: 'R1002', motion: { id: 2, text: 'This house would...' } },
];

const MOCK_MOTIONS = [
  { id: 1, text: 'This house believes...' },
  { id: 2, text: 'This house would...' },
];

// Mock user service if not available
const fetchUsers = async () => {
  try {
    // First try the actual service if it exists
    try {
      const userServiceModule = await import('../services/userService');
      if (userServiceModule?.fetchUsers) {
        const result = await userServiceModule.fetchUsers();
        if (Array.isArray(result) && result.length > 0) {
          return result;
        }
      }
    } catch (err) {
      console.log("Falling back to mock user data due to error:", err);
    }
    
    // Always return mock data if service fails or returns empty
    console.log("Using mock user data");
    return MOCK_USERS;
  } catch (error) {
    console.error("Error fetching users:", error);
    return MOCK_USERS;
  }
};

// Mock round service if not available
const fetchRounds = async () => {
  try {
    // First try the actual service if it exists
    try {
      const roundServiceModule = await import('../services/roundService');
      if (roundServiceModule?.fetchRounds) {
        const result = await roundServiceModule.fetchRounds();
        if (Array.isArray(result) && result.length > 0) {
          return result;
        }
      }
    } catch (err) {
      console.log("Falling back to mock round data due to error:", err);
    }
    
    // Always return mock data if service fails or returns empty
    console.log("Using mock round data");
    return MOCK_ROUNDS;
  } catch (error) {
    console.error("Error fetching rounds:", error);
    return MOCK_ROUNDS;
  }
};

// Mock motion service if not available
const fetchMotions = async () => {
  try {
    // First try the actual service if it exists
    try {
      const motionServiceModule = await import('../services/motionService');
      if (motionServiceModule?.fetchMotions) {
        const result = await motionServiceModule.fetchMotions();
        if (Array.isArray(result) && result.length > 0) {
          return result;
        }
      }
    } catch (err) {
      console.log("Falling back to mock motion data due to error:", err);
    }
    
    // Always return mock data if service fails or returns empty
    console.log("Using mock motion data");
    return MOCK_MOTIONS;
  } catch (error) {
    console.error("Error fetching motions:", error);
    return MOCK_MOTIONS;
  }
};

interface User {
  id: number;
  username: string;
}

interface Round {
  id: number;
  round_code: string;
  motion?: {
    id: number;
    text: string;
  };
}

interface Motion {
  id: number;
  text: string;
}

const AdminLogsPanel: React.FC = () => {
  const navigate = useNavigate();
  const { pageTransition } = useTheme();
  const [activeTab, setActiveTab] = useState("attendance");
  const [logs, setLogs] = useState<AnyLogDateGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  // For filtering
  const [users, setUsers] = useState<User[]>(MOCK_USERS);
  const [rounds, setRounds] = useState<Round[]>(MOCK_ROUNDS);
  const [motions, setMotions] = useState<Motion[]>(MOCK_MOTIONS);
  const [filters, setFilters] = useState<LogFilters>({});
  
  // Load reference data
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [usersData, roundsData, motionsData] = await Promise.all([
          fetchUsers(),
          fetchRounds(),
          fetchMotions()
        ]);
        
        if (Array.isArray(usersData)) setUsers(usersData);
        if (Array.isArray(roundsData)) setRounds(roundsData);
        if (Array.isArray(motionsData)) setMotions(motionsData);
      } catch (err: any) {
        console.error("Error loading reference data:", err);
        toast.error("Failed to load filter data, using mock data instead");
      }
    };
    
    loadReferenceData();
  }, []);
  
  // Load logs when tab changes or filters change
  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        let data: AnyLogDateGroup[] = [];
        
        if (activeTab === 'attendance') {
          data = await fetchActivityLogs(filters).catch(err => {
            console.error("Error fetching activity logs:", err);
            return [];
          });
        } else {
          data = await fetchSystemLogs(filters).catch(err => {
            console.error("Error fetching system logs:", err);
            return [];
          });
        }
        
        // Ensure data is always an array
        if (!Array.isArray(data)) {
          console.warn("Logs API did not return an array, using empty array instead");
          data = [];
        }
        
        setLogs(data);
        setError('');
      } catch (err: any) {
        setError('Failed to load logs: ' + (err.message || 'Unknown error'));
        console.error("Error loading logs:", err);
        setLogs([]); // Ensure logs is set to an empty array on error
      } finally {
        setLoading(false);
      }
    };
    
    fetchLogs();
  }, [activeTab, filters]);
  
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    // Reset filters when changing tabs
    setFilters({});
  };
  
  const handleExportCSV = async () => {
    if (logs.length === 0) {
      toast.error("No logs to export");
      return;
    }
    
    setExporting(true);
    
    try {
      if (activeTab === 'attendance') {
        await exportActivityLogsCSV(filters);
      } else {
        await exportSystemLogsCSV(filters);
      }
      toast.success("Logs exported successfully");
    } catch (err: any) {
      console.error("Export error:", err);
      toast.error(`Export failed: ${err.message || 'Unknown error'}`);
    } finally {
      setExporting(false);
    }
  };
  
  const handleFilterChange = (name: keyof LogFilters, value: any) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  
  const clearFilters = () => {
    setFilters({});
  };
  
  const getTotalLogCount = (): number => {
    if (!Array.isArray(logs)) return 0;
    return logs.reduce((total, day) => {
      if (!day || !Array.isArray(day.logs)) return total;
      return total + day.logs.length;
    }, 0);
  };
  
  const getStatusColor = (logType: string) => {
    switch (logType) {
      case 'ERROR':
        return 'text-red-600 dark:text-red-400';
      case 'WARNING':
        return 'text-amber-600 dark:text-amber-400';
      case 'INFO':
        return 'text-blue-600 dark:text-blue-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };
  
  const renderFilterPanel = () => {
    if (activeTab === 'attendance') {
      // Filters for attendance logs
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <Label htmlFor="user_filter">User</Label>
            <Select
              value={filters.user_id?.toString() || ''}
              onValueChange={(value) => handleFilterChange('user_id', value ? parseInt(value) : undefined)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select user" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="">All Users</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id.toString()}>
                      {user.username}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="round_filter">Round</Label>
            <Select
              value={filters.round_id?.toString() || ''}
              onValueChange={(value) => handleFilterChange('round_id', value ? parseInt(value) : undefined)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select round" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="">All Rounds</SelectItem>
                  {rounds.map(round => (
                    <SelectItem key={round.id} value={round.id.toString()}>
                      Round {round.id} ({round.round_code})
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="motion_filter">Motion</Label>
            <Select
              value={filters.motion_id?.toString() || ''}
              onValueChange={(value) => handleFilterChange('motion_id', value ? parseInt(value) : undefined)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select motion" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="">All Motions</SelectItem>
                  {motions.map(motion => (
                    <SelectItem key={motion.id} value={motion.id.toString()}>
                      {motion.text.length > 40 ? motion.text.substring(0, 40) + '...' : motion.text}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="role_filter">Role</Label>
            <Select
              value={filters.role || ''}
              onValueChange={(value) => handleFilterChange('role', value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="">All Roles</SelectItem>
                  <SelectItem value="Speaker">Speaker</SelectItem>
                  <SelectItem value="Adjudicator">Adjudicator</SelectItem>
                  <SelectItem value="Observer">Observer</SelectItem>
                  <SelectItem value="Moderator">Moderator</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="action_filter">Action</Label>
            <Select
              value={filters.action || ''}
              onValueChange={(value) => handleFilterChange('action', value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select action" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="">All Actions</SelectItem>
                  <SelectItem value="joined">Joined Round</SelectItem>
                  <SelectItem value="allocated">Allocated</SelectItem>
                  <SelectItem value="completed">Completed Round</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          
          <div className="col-span-1 md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date_from">Date From</Label>
              <Input
                id="date_from"
                type="date"
                value={filters.date_from || ''}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <Label htmlFor="date_to">Date To</Label>
              <Input
                id="date_to"
                type="date"
                value={filters.date_to || ''}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full"
              />
            </div>
          </div>
          
          <div className="col-span-1 md:col-span-3 flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={clearFilters}
              className="flex items-center gap-1"
            >
              <FilterX className="h-4 w-4" />
              Clear Filters
            </Button>
            
            <Button
              variant="default"
              onClick={() => setIsFilterOpen(false)}
              className="flex items-center gap-1"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      );
    } else {
      // Filters for system logs
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <Label htmlFor="log_type_filter">Log Type</Label>
            <Select
              value={filters.log_type || ''}
              onValueChange={(value) => handleFilterChange('log_type', value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select log type" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="">All Types</SelectItem>
                  <SelectItem value="INFO">Information</SelectItem>
                  <SelectItem value="WARNING">Warning</SelectItem>
                  <SelectItem value="ERROR">Error</SelectItem>
                  <SelectItem value="ACTION">User Action</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="user_filter">User</Label>
            <Select
              value={filters.user_id?.toString() || ''}
              onValueChange={(value) => handleFilterChange('user_id', value ? parseInt(value) : undefined)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select user" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="">All Users</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id.toString()}>
                      {user.username}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="activity_filter">Activity</Label>
            <Input
              id="activity_filter"
              type="text"
              placeholder="Filter by activity"
              value={filters.activity || ''}
              onChange={(e) => handleFilterChange('activity', e.target.value)}
              className="w-full"
            />
          </div>
          
          <div className="col-span-1 md:col-span-3">
            <Label htmlFor="message_filter">Message</Label>
            <Input
              id="message_filter"
              type="text"
              placeholder="Search in messages"
              value={filters.message || ''}
              onChange={(e) => handleFilterChange('message', e.target.value)}
              className="w-full"
            />
          </div>
          
          <div className="col-span-1 md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date_from">Date From</Label>
              <Input
                id="date_from"
                type="date"
                value={filters.date_from || ''}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <Label htmlFor="date_to">Date To</Label>
              <Input
                id="date_to"
                type="date"
                value={filters.date_to || ''}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full"
              />
            </div>
          </div>
          
          <div className="col-span-1 md:col-span-3 flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={clearFilters}
              className="flex items-center gap-1"
            >
              <FilterX className="h-4 w-4" />
              Clear Filters
            </Button>
            
            <Button
              variant="default"
              onClick={() => setIsFilterOpen(false)}
              className="flex items-center gap-1"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      );
    }
  };
  
  const renderAttendanceLogs = () => {
    if (!Array.isArray(logs) || logs.length === 0) {
      return (
        <div className="p-4 text-center text-gray-500 dark:text-gray-400">
          No attendance logs found{Object.keys(filters).length > 0 ? ' matching your filters' : ''}
        </div>
      );
    }
    
    // Filter only ActivityLog entries using type guard
    const activityLogs = logs.filter((item): item is LogDateGroup<ActivityLog> => {
      if (!item || !item.logs || !Array.isArray(item.logs) || item.logs.length === 0) return false;
      // Check if the first log has ActivityLog properties
      const firstLog = item.logs[0];
      return firstLog && 'round' in firstLog && 'role' in firstLog;
    });
    
    return (
      <div className="space-y-6">
        {activityLogs.map((dayGroup) => (
          <div key={dayGroup.date} className="border dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="bg-gray-100 dark:bg-gray-700 p-3 font-medium flex justify-between items-center">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                {format(new Date(dayGroup.date), 'MMMM d, yyyy')}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {dayGroup.logs.length} {dayGroup.logs.length === 1 ? 'entry' : 'entries'}
              </div>
            </div>
            
            <TableContainer>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800">
                    <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">User</th>
                    <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Round</th>
                    <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Role</th>
                    <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Action</th>
                    <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {dayGroup.logs.map((log) => (
                    <tr key={log.id} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750">
                      <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">{log.user?.username}</td>
                      <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">
                        {log.round ? `Round ${log.round.id}${log.round.motion ? `: ${log.round.motion.text.substring(0, 30)}...` : ''}` : 'N/A'}
                      </td>
                      <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">{log.role}</td>
                      <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">{log.action}</td>
                      <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">
                        {format(new Date(log.timestamp), 'h:mm a')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableContainer>
          </div>
        ))}
      </div>
    );
  };
  
  const renderSystemLogs = () => {
    if (!Array.isArray(logs) || logs.length === 0) {
      return (
        <div className="p-4 text-center text-gray-500 dark:text-gray-400">
          No system logs found{Object.keys(filters).length > 0 ? ' matching your filters' : ''}
        </div>
      );
    }
    
    // Filter only SystemLog entries using type guard
    const systemLogs = logs.filter((item): item is LogDateGroup<SystemLog> => {
      if (!item || !item.logs || !Array.isArray(item.logs) || item.logs.length === 0) return false;
      // Check if the first log has SystemLog properties
      const firstLog = item.logs[0];
      return firstLog && 'log_type' in firstLog && 'activity' in firstLog;
    });
    
    return (
      <div className="space-y-6">
        {systemLogs.map((dayGroup) => (
          <div key={dayGroup.date} className="border dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="bg-gray-100 dark:bg-gray-700 p-3 font-medium flex justify-between items-center">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                {format(new Date(dayGroup.date), 'MMMM d, yyyy')}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {dayGroup.logs.length} {dayGroup.logs.length === 1 ? 'entry' : 'entries'}
              </div>
            </div>
            
            <TableContainer>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800">
                    <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Type</th>
                    <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Message</th>
                    <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">User</th>
                    <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Activity</th>
                    <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {dayGroup.logs.map((log) => (
                    <tr key={log.id} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750">
                      <td className={`p-2 text-sm font-medium ${getStatusColor(log.log_type)}`}>{log.log_type}</td>
                      <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">{log.message}</td>
                      <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">{log.user?.username || 'System'}</td>
                      <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">{log.activity}</td>
                      <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">
                        {format(new Date(log.timestamp), 'h:mm a')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableContainer>
          </div>
        ))}
      </div>
    );
  };
  
  return (
    <div className="p-4 w-full max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between mb-6 gap-4">
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            className="mr-2" 
            onClick={() => pageTransition(() => navigate('/dashboard'))}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold dark:text-white light:text-gray-800">
            Activity Logs
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="flex items-center gap-1"
          >
            {isFilterOpen ? <X className="h-4 w-4" /> : <Filter className="h-4 w-4" />}
            {isFilterOpen ? 'Hide Filters' : 'Show Filters'}
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setFilters({})}
            disabled={loading}
            className="flex items-center gap-1"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Button 
            variant="secondary" 
            size="sm"
            onClick={handleExportCSV}
            disabled={logs.length === 0 || loading || exporting}
            className="flex items-center gap-1"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>
      
      {/* Filter Panel */}
      <Collapsible open={isFilterOpen} onOpenChange={setIsFilterOpen} className="mb-6">
        <CollapsibleContent>
          <Card className="shadow-sm dark:bg-gray-800/50 light:bg-white mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center">
                <Filter className="h-4 w-4 mr-2" />
                Filter Logs
                {Object.keys(filters).length > 0 && (
                  <span className="ml-2 text-sm bg-blue-500 text-white rounded-full px-2 py-0.5">
                    {Object.keys(filters).length}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {renderFilterPanel()}
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
      
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid grid-cols-2 mb-8 max-w-md">
          <TabsTrigger value="attendance">Attendance Logs</TabsTrigger>
          <TabsTrigger value="system">System Logs</TabsTrigger>
        </TabsList>
        
        <TabsContent value="attendance">
          <Card className="shadow-lg dark:bg-gray-800 light:bg-white">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="dark:text-white light:text-gray-800">
                Participant Attendance Records
                {getTotalLogCount() > 0 && (
                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                    ({getTotalLogCount()} entries)
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : error ? (
                <div className="p-4 bg-red-500 bg-opacity-10 border border-red-500 rounded-lg">
                  <p className="text-red-500">{error}</p>
                </div>
              ) : (
                renderAttendanceLogs()
              )}
              
              <p className="text-xs text-gray-500 mt-6">
                Note: These logs track user participation across rounds and are used for attendance verification.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="system">
          <Card className="shadow-lg dark:bg-gray-800 light:bg-white">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="dark:text-white light:text-gray-800">
                System Activity Logs
                {getTotalLogCount() > 0 && (
                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                    ({getTotalLogCount()} entries)
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : error ? (
                <div className="p-4 bg-red-500 bg-opacity-10 border border-red-500 rounded-lg">
                  <p className="text-red-500">{error}</p>
                </div>
              ) : (
                renderSystemLogs()
              )}
              
              <p className="text-xs text-gray-500 mt-6">
                Note: System logs track important events and actions in the application for troubleshooting and auditing purposes.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminLogsPanel;